import { acl } from '@/modules/auth/mixins/acl'
import { showAlertMessage } from '@/helpers/helpers'
import { formatXcalacocoDateTime } from '@/helpers/xcalacocoBookingsHelper'

const FALLBACK_MESSAGE = 'Ocurrió un error al procesar la solicitud.'
const STALE_MESSAGE = 'La reserva cambió desde que la abriste; revísala y vuelve a confirmar.'
const RELOAD_CODES = ['invalidTransition', 'stale']
const CLOSE_CODES = RELOAD_CODES.concat(['forbidden', 'notFound'])
const CANCEL_PENDING = '6'
// Dialog config, request body and success message per status mode; approve and reject act on a pending cancellation ('6').
// Each view adds its own `confirm` mode
const CANCEL_MODES = {
  cancel: {
    title: 'Cancelar reserva',
    label: 'Motivo de cancelación',
    button: 'Cancelar reserva',
    variant: 'danger',
    multiline: true,
    body: (booking, text) => ({ id: booking.id, status: '3', cancelReason: text }),
    success: 'Reserva cancelada',
  },
  approveCancel: {
    title: 'Aprobar cancelación',
    hint: 'La reserva quedará cancelada con el motivo del propietario.',
    button: 'Aprobar cancelación',
    variant: 'danger',
    showRequest: true,
    body: booking => ({ id: booking.id, status: 'approveCancel' }),
    success: 'Cancelación aprobada',
  },
  rejectCancel: {
    title: 'Rechazar cancelación',
    hint: 'La reserva volverá a quedar Confirmada.',
    label: 'Motivo del rechazo',
    button: 'Rechazar cancelación',
    variant: 'primary',
    multiline: true,
    showRequest: true,
    body: (booking, text) => ({ id: booking.id, status: 'rejectCancel', rejectReason: text }),
    success: 'Cancelación rechazada',
  },
}

// Review logic shared by the Xcalacoco stays and Beach Club detail views. The view provides:
// - data: listTab (the OwnersBooking tab to go back to), confirmMode, actionPermissions ({ edit, confirm, cancel });
// - methods: fetchBooking(id), setBookingStatus(body), updateBooking(body), usually aliased with mapActions.
// eslint-disable-next-line import/prefer-default-export
export const ownerBookingDetail = {
  mixins: [acl],
  data() {
    return {
      booking: null,
      isLoading: false,
      isSaving: false,
      statusModal: { visible: false, mode: 'confirm' },
      editModal: { visible: false },
    }
  },
  computed: {
    status() {
      return this.booking ? String(this.booking.status) : ''
    },
    statusModes() {
      return { confirm: this.confirmMode, ...CANCEL_MODES }
    },
    canEdit() {
      return ['1', '2'].includes(this.status) && this.can(this.actionPermissions.edit)
    },
    canConfirm() {
      return this.status === '1' && this.can(this.actionPermissions.confirm)
    },
    canCancel() {
      return ['1', '2'].includes(this.status) && this.can(this.actionPermissions.cancel)
    },
    canReviewCancel() {
      return this.status === CANCEL_PENDING && this.can(this.actionPermissions.cancel)
    },
  },
  methods: {
    async loadBooking() {
      this.isLoading = true
      const result = await this.fetchBooking(this.$route.params.id)
      this.isLoading = false
      if (result.ok) {
        this.booking = result.data || null
        return
      }
      // Keep what is on screen when a reload fails; only a real not-found clears it
      if (result.code === 'notFound') this.booking = null
      else showAlertMessage('Atención', 'InfoIcon', result.message || FALLBACK_MESSAGE, 'warning', 5000)
    },
    goBack() {
      this.$router.push({ name: 'OwnersBooking', query: { ...this.$route.query, tab: this.listTab } })
    },
    errorMessage(result) {
      if (result.code === 'stale') return STALE_MESSAGE
      return result.message || FALLBACK_MESSAGE
    },
    // Returns true when the open modal must be closed
    handleResult(result, successMessage) {
      if (result.ok) {
        showAlertMessage('Listo', 'CheckIcon', successMessage, 'success')
        this.loadBooking()
        return true
      }
      showAlertMessage('Atención', 'InfoIcon', this.errorMessage(result), 'warning', 5000)
      if (RELOAD_CODES.includes(result.code)) this.loadBooking()
      if (result.code === 'notFound') this.goBack()
      return CLOSE_CODES.includes(result.code)
    },
    openStatusModal(mode) {
      this.statusModal = { visible: true, mode }
    },
    closeStatusModal() {
      if (this.isSaving) return
      this.statusModal = { ...this.statusModal, visible: false }
    },
    // Idempotent: the edit modals emit close from Cerrar and again from their hidden event
    closeEditModal() {
      if (this.isSaving) return
      this.editModal = { visible: false }
    },
    // Views override this to add details from the saved booking
    updatedMessage() {
      return 'Reserva actualizada'
    },
    async saveBooking(body) {
      if (this.isSaving) return
      this.isSaving = true
      const result = await this.updateBooking(body)
      this.isSaving = false
      if (this.handleResult(result, this.updatedMessage(result))) this.closeEditModal()
    },
    async submitStatus(text) {
      const { booking } = this
      if (!booking || this.isSaving) return
      const action = this.statusModes[this.statusModal.mode]
      if (!action) return
      this.isSaving = true
      const result = await this.setBookingStatus(action.body(booking, text))
      this.isSaving = false
      if (this.handleResult(result, action.success)) this.closeStatusModal()
    },
    // The owner's request stays on the booking after a rejection, so it shows whenever it exists
    cancelRequestSection(booking) {
      if (String(booking.status) !== CANCEL_PENDING && !booking.cancelRequestNotes && !booking.cancellationRequestDate) return []
      return [{
        title: 'Solicitud de cancelación',
        fields: [
          { label: 'Fecha de solicitud', value: formatXcalacocoDateTime(booking.cancellationRequestDate) },
          { label: 'Motivo del propietario', value: booking.cancelRequestNotes, wide: true },
        ],
      }]
    },
    // 0 people is a real value, so only empty values fall back to a dash
    displayValue(value) {
      return value === null || value === undefined || value === '' ? '—' : value
    },
  },
}
